/**
 * Drivers trophies (design system v1, Sep 2026) — drawn in code, not uploaded
 * images, so they follow the theme and read as one set.
 *
 *   <Trophy>  the cabinet statue: a cup in the value's colour with its emblem.
 *             Lit once the user has a released nomination for that value;
 *             otherwise an outlined "empty plinth" in line colours.
 *   <Medal>   the award won in a released month: a ribbon medal.
 *
 * Each value has a colour (chart palette, so light + dark are tuned) and a
 * Lucide emblem. Unknown/custom values fall back to teal + a star.
 */
import { useId } from 'react';
import { Gem, HeartHandshake, Rocket, ShieldCheck, Star, type LucideIcon } from 'lucide-react';

const VALUE_STYLE: Record<string, { color: string; emblem: LucideIcon }> = {
  drive: { color: '--chart-5', emblem: Rocket },
  reliability: { color: '--chart-3', emblem: ShieldCheck },
  authenticity: { color: '--chart-6', emblem: Gem },
  empathy: { color: '--chart-2', emblem: HeartHandshake },
};
const FALLBACK = { color: '--chart-4', emblem: Star };

export function valueStyle(valueId: string) {
  return VALUE_STYLE[valueId] ?? FALLBACK;
}

export function Trophy({ valueId, lit, size = 96, label }: { valueId: string; lit: boolean; size?: number; label?: string }) {
  const uid = useId().replace(/:/g, '');
  const { color, emblem: Emblem } = valueStyle(valueId);
  const c = `rgb(var(${color}))`;
  const cup = 'M26 12 H70 V30 C70 47 60 58 48 58 C36 58 26 47 26 30 Z';
  return (
    <svg
      width={size}
      height={size * (112 / 96)}
      viewBox="0 0 96 112"
      role="img"
      aria-label={label ?? (lit ? 'Trophy' : 'Trophy not earned yet')}
      className={lit ? 'trophy trophy-lit' : 'trophy'}
    >
      <defs>
        <linearGradient id={`g${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: c, stopOpacity: 0.55 }} />
          <stop offset="1" style={{ stopColor: c, stopOpacity: 1 }} />
        </linearGradient>
        <clipPath id={`c${uid}`}><path d={cup} /></clipPath>
      </defs>
      {/* soft glow behind a lit cup */}
      {lit && <ellipse cx="48" cy="40" rx="40" ry="36" style={{ fill: c, opacity: 0.12 }} />}
      {/* handles */}
      <path
        d="M27 18 H18 C13 18 11 22 11 26 C11 35 18 41 28 42 M69 18 H78 C83 18 85 22 85 26 C85 35 78 41 68 42"
        fill="none"
        strokeWidth="4"
        strokeLinecap="round"
        style={{ stroke: lit ? c : 'rgb(var(--line-strong))' }}
      />
      {/* cup */}
      <path
        d={cup}
        style={lit ? { fill: `url(#g${uid})` } : { fill: 'rgb(var(--surface-2))', stroke: 'rgb(var(--line-strong))', strokeWidth: 2, strokeDasharray: '4 3' }}
      />
      {/* shine sweep (lit only, animates on hover via .trophy-lit:hover) */}
      {lit && (
        <g clipPath={`url(#c${uid})`}>
          <rect className="trophy-shine" x="-30" y="0" width="14" height="70" transform="skewX(-18)" fill="white" opacity="0.35" />
        </g>
      )}
      {/* rim */}
      <rect x="24" y="9" width="48" height="5" rx="2.5" style={{ fill: lit ? c : 'rgb(var(--line-strong))' }} />
      {/* emblem */}
      <Emblem x={37} y={22} width={22} height={22} strokeWidth={2.25} color={lit ? 'white' : 'rgb(var(--fg-faint))'} />
      {/* stem + plinth */}
      <rect x="43" y="58" width="10" height="14" style={{ fill: lit ? c : 'rgb(var(--line-strong))' }} />
      <rect x="32" y="72" width="32" height="8" rx="2" style={{ fill: lit ? c : 'rgb(var(--line-strong))', opacity: lit ? 0.85 : 1 }} />
      <rect x="24" y="80" width="48" height="14" rx="3" style={lit ? { fill: '#2A2624', stroke: 'rgb(var(--line-strong))', strokeWidth: 1 } : { fill: 'rgb(var(--line-strong))', opacity: 0.6 }} />
      {lit && <rect x="36" y="85" width="24" height="3" rx="1.5" style={{ fill: c, opacity: 0.9 }} />}
    </svg>
  );
}

export function Medal({ valueId, size = 88, label }: { valueId: string; size?: number; label?: string }) {
  const uid = useId().replace(/:/g, '');
  const { color, emblem: Emblem } = valueStyle(valueId);
  const c = `rgb(var(${color}))`;
  return (
    <svg width={size} height={size * (104 / 88)} viewBox="0 0 88 104" role="img" aria-label={label ?? 'Award'} className="trophy trophy-lit">
      <defs>
        <radialGradient id={`m${uid}`} cx="0.35" cy="0.3" r="0.8">
          {/* fixed gold: reads as gold on both themes (the amber chart token goes bronze in light) */}
          <stop offset="0" stopColor="#FBE08A" />
          <stop offset="0.55" stopColor="#E7B53C" />
          <stop offset="1" stopColor="#B9851C" />
        </radialGradient>
      </defs>
      {/* ribbon tails */}
      <path d="M30 4 L44 40 L34 44 L20 8 Z" style={{ fill: c }} />
      <path d="M58 4 L44 40 L54 44 L68 8 Z" style={{ fill: c, opacity: 0.8 }} />
      {/* medal */}
      <circle cx="44" cy="66" r="30" style={{ fill: `url(#m${uid})` }} />
      <circle cx="44" cy="66" r="23" fill="none" stroke="white" strokeOpacity="0.55" strokeWidth="2" strokeDasharray="1 3.2" strokeLinecap="round" />
      <circle cx="44" cy="66" r="17" style={{ fill: c }} />
      <Emblem x={33} y={55} width={22} height={22} strokeWidth={2.25} color="white" />
    </svg>
  );
}
